import React, { memo } from "react";
import { Tr } from "@chakra-ui/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ITableItem } from "./types";

interface SortableRowProps<T extends ITableItem> {
  item: T;
  children: React.ReactNode;
  rowStyle?: React.CSSProperties;
  canDrag: boolean;
  dragStyle?: React.CSSProperties;
}

const SortableRow = memo(
  <T extends ITableItem>({
    item,
    children,
    rowStyle,
    canDrag,
    dragStyle,
  }: SortableRowProps<T>) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: item.id ?? `item-${Math.random()}-${Date.now()}`,
      disabled: !canDrag,
    });

    const baseStyle = canDrag
      ? {
          transform: CSS.Transform.toString(transform),
          transition,
          touchAction: "none",
          cursor: isDragging ? "grabbing" : "grab",
        }
      : {};

    const draggingStyle = isDragging
      ? {
          backgroundColor: "rgba(66,153,225,0.2)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          opacity: 0.8,
          ...dragStyle,
        }
      : {};

    return (
      <Tr
        ref={setNodeRef}
        style={{ ...rowStyle, ...baseStyle, ...draggingStyle }}
        {...(canDrag ? { ...attributes, ...listeners } : {})}
      >
        {children}
      </Tr>
    );
  }
);

export default SortableRow;
